/**
 * Customers and their accounts.
 *
 * Network-only, deliberately. Every other repository in this codebase reads
 * through Dexie so a till keeps working with no signal, and this one does not,
 * because a credit limit checked against a local copy is a credit limit that has
 * already been spent somewhere else. §4.8 makes credit online-only; a local
 * cache here would be the first step towards quietly softening that.
 */

import { api } from "@/api/client";

export interface StoreAccount {
  id: string;
  credit_limit: string;
  balance: string;
  available_credit: string;
  is_over_limit: boolean;
  is_active: boolean;
  suspended_reason: string;
}

export interface AccountEntry {
  id: string;
  entry_type: "CHARGE" | "PAYMENT" | "ADJUSTMENT" | "WRITE_OFF" | "REFUND";
  amount: string;
  balance_after: string;
  reason: string;
  receipt_number: string;
  by: string | null;
  created_at: string;
}

export interface Consent {
  id: string;
  purpose: string;
  granted: boolean;
  channel: string;
  evidence: string;
  recorded_by_name: string | null;
  created_at: string;
}

export interface Customer {
  id: string;
  name: string;
  phone: string;
  email: string;
  note: string;
  loyalty_points: number;
  is_active: boolean;
  account: StoreAccount | null;
  created_at: string;
}

export interface CustomerDetail extends Customer {
  entries: AccountEntry[];
  consents: Consent[];
}

interface Page<T> {
  count: number;
  next: string | null;
  results: T[];
}

function unwrap<T>(payload: Page<T> | T[]): T[] {
  return Array.isArray(payload) ? payload : payload.results;
}

export const customersRepository = {
  list: async (params: { q?: string; onAccount?: boolean } = {}): Promise<Customer[]> => {
    const query = new URLSearchParams();
    if (params.q) query.set("q", params.q);
    if (params.onAccount) query.set("on_account", "true");
    return unwrap(await api.get<Page<Customer> | Customer[]>(`/customers/?${query.toString()}`));
  },

  get: (id: string) => api.get<CustomerDetail>(`/customers/${id}/`),

  create: (body: { name: string; phone?: string; email?: string; note?: string }) =>
    api.post<Customer>("/customers/", body),

  update: (
    id: string,
    body: Partial<{ name: string; phone: string; email: string; note: string }>,
  ) => api.patch<Customer>(`/customers/${id}/`, body),

  setCreditLimit: (id: string, limit: string, reason: string) =>
    api.post<StoreAccount>(`/customers/${id}/credit-limit/`, { limit, reason }),

  pay: (id: string, amount: string, reason: string) =>
    api.post<StoreAccount>(`/customers/${id}/pay/`, { amount, reason }),

  suspend: (id: string, reason: string) =>
    api.post<StoreAccount>(`/customers/${id}/suspend/`, { reason }),

  recordConsent: (
    id: string,
    body: { purpose: string; granted: boolean; channel: string; evidence?: string },
  ) => api.post<Consent>(`/customers/${id}/consent/`, body),

  personalData: (id: string) => api.get<Record<string, unknown>>(`/customers/${id}/personal-data/`),

  anonymise: (id: string) => api.post<Customer>(`/customers/${id}/anonymise/`, {}),
};
